import React, { createContext, useContext, useState } from 'react';
import { Order } from '../types/order';
import {
  createOrder,
  deleteOrder,
  fetchOrders,
  updateOrder,
  fetchOrdersByStatus as fetchOrdersByStatusApi,
  updateOrderStatus,
  downloadOrdersPdf as downloadOrdersPdfApi
} from '../api/orderApi';
import { handleWithAlert } from '../utils/handleWithAlert';

interface OrderContextType {
  orders: Order[];
  addOrder: (order: Partial<Order>) => void;
  updateOrder: (order: Order) => void;
  deleteOrder: (id: string) => void;
  listOrders: () => Promise<void>;
  fetchOrdersByStatus: (status: string) => Promise<void>;
  changeOrderStatus: (id: string, status: string) => Promise<void>;
  downloadOrdersPdf: (orderIds: string[], lang: string) => Promise<Blob>;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const OrderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);

  const listOrders = async () => {
    const data = await handleWithAlert(() => fetchOrders());
    if (data) setOrders(data);
  };

  const addOrder = async (order: Partial<Order>) => {
    const created = await handleWithAlert(() => createOrder(order), {
      successMessage: "Order created successfully!",
      errorMessage: "Failed to create order",
      showSuccess: true,
    });
    if (created) setOrders(prev => [...prev, created]);
  };

  const updateOrderFn = async (order: Order) => {
    const updated = await handleWithAlert(() => updateOrder(order.id, order), {
      successMessage: "Order updated successfully!",
      errorMessage: "Failed to update order",
      showSuccess: true,
    });
    if (updated) setOrders(prev => prev.map(o => o.id === order.id ? updated : o));
  };

  const deleteOrderFn = async (id: string) => {
    const deleted = await handleWithAlert(() => deleteOrder(id), {
      successMessage: "Order deleted successfully!",
      errorMessage: "Failed to delete order",
      showSuccess: true,
    });
    if (deleted !== null) setOrders(prev => prev.filter(o => o.id !== id));
  };

  const fetchOrdersByStatus = async (status: string) => {
    const data = await handleWithAlert(() => fetchOrdersByStatusApi(status), {
      errorMessage: "Failed to fetch orders by status"
    });
    if (data) setOrders(data);
  };

  const changeOrderStatus = async (id: string, status: string) => {
    const updated = await handleWithAlert(() => updateOrderStatus(id, status), {
      successMessage: "Order status updated successfully!",
      errorMessage: "Failed to update order status",
      showSuccess: true,
    });
    if (updated) setOrders(prev => prev.map(o => o.id === id ? { ...o, status: updated.status } : o));
  };

  // === PDF Download Context Function ===
  const downloadOrdersPdf = async (orderIds: string[], lang: string) => {
    return await downloadOrdersPdfApi(orderIds, lang);
  };

  return (
    <OrderContext.Provider value={{
      orders,
      addOrder,
      updateOrder: updateOrderFn,
      deleteOrder: deleteOrderFn,
      listOrders,
      fetchOrdersByStatus,
      changeOrderStatus,
      downloadOrdersPdf,
    }}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) throw new Error('useOrder must be inside OrderProvider');
  return context;
};
